import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    CircularProgress,
    TextField,
    Button,
    MenuItem,
    Divider,
    Snackbar,
    Alert,
    Select,
    FormControl,
    InputLabel,
    FormControlLabel,
    Checkbox,
    Autocomplete,
} from '@mui/material';
import DataGridComponent from 'components/DataGridComponent';
import { DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { sortMultipleFields } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const calcPerdiemFromHours = (travelHours, params) => {
    const minMinutes = Number(params?.MINIMUM_PERDIEM_MINUTES) || 360;
    const maxMinutes = Infinity;
    const method = params?.PER_DIEM_CALC_METHOD || 'DAILY';

    const travelMinutes = (travelHours || 0) * 60;
    if (travelMinutes <= minMinutes) return 0;
    if (travelMinutes > maxMinutes) return 0;

    if (method === 'HOURLY') return travelMinutes / 60;

    const BLOCK_MINUTES = 720;
    const BLOCK_DAYS = 0.5;
    let perdiemDays = BLOCK_DAYS;
    let threshold = BLOCK_MINUTES;
    while (travelMinutes >= threshold) {
        perdiemDays += BLOCK_DAYS;
        threshold += BLOCK_MINUTES;
    }
    return perdiemDays;
};


const TravelAllowanceEntryForm = () => {
    useAuthCheck();
    const { travelAllowanceEntryId } = useParams();
    const navigate = useNavigate();
    const reduxParams = useSelector((state) => state.params);
    const crewRanksData = useSelector((state) => state.crewRanks);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [countries, setCountries] = useState([]);
    const [airports, setAirports] = useState([]);
    const [crewMembers, setCrewMembers] = useState([]);
    const [search, setSearch] = useState('');
    const [rankFilter, setRankFilter] = useState('');
    const [baseFilter, setBaseFilter] = useState('');
    const [deptFilter, setDeptFilter] = useState('');
    const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });

    const paramsMap = useMemo(
        () => new Map((reduxParams ?? []).map(p => [p.param, p.value])),
        [reduxParams]
    );

    const perdiemParams = useMemo(() => ({
        MINIMUM_PERDIEM_MINUTES: paramsMap.get('MINIMUM_PERDIEM_MINUTES'),
        MAXIMUM_PERDIEM_MINUTES: paramsMap.get('MAXIMUM_PERDIEM_MINUTES'),
        PER_DIEM_CALC_METHOD: paramsMap.get('PER_DIEM_CALC_METHOD'),
    }), [paramsMap]);

    const sortedRankOptions = useMemo(() => {
        const rankMap = new Map((crewRanksData || []).map(r => [r.rankCode, r.rankPriority ?? 999]));
        return [...new Set(crewMembers.map(c => c.rank).filter(Boolean))]
            .sort((a, b) => (rankMap.get(a) ?? 999) - (rankMap.get(b) ?? 999));
    }, [crewMembers, crewRanksData]);

    const filteredCrew = useMemo(() => {
        const s = search.toLowerCase();
        return crewMembers.filter(c => {
            const matchSearch = !s || c.crewCode?.toLowerCase().includes(s) || c.crewName?.toLowerCase().includes(s);
            const matchRank = !rankFilter || c.rank === rankFilter;
            const matchBase = !baseFilter || c.location === baseFilter;
            const matchDept = !deptFilter || c.department === deptFilter;
            return matchSearch && matchRank && matchBase && matchDept;
        });
    }, [crewMembers, search, rankFilter, baseFilter, deptFilter]);

    const [formValues, setFormValues] = useState({
        destination: '',
        travelZone: '',
        isInternational: false,
        startDate: null,
        endDate: null,
        travelHours: 0,
        description: '',
        status: 'approved',
    });


    const domesticAirportSet = useMemo(() => {
        const domesticCountryCodes = new Set(
            countries.filter(c => c.isDefault).map(c => c.countryCode)
        );
        return new Set(
            airports.filter(a => domesticCountryCodes.has(a.countryCode)).map(a => a.iataCode)
        );
    }, [countries, airports]);

    const fetchCountries = useCallback(async () => {
        return api.get('/countries');
    }, []);

    const fetchAirports = useCallback(async () => {
        return api.get('/airports/airportswithtravelzone');
    }, []);

    const fetchCrewMembers = useCallback(async () => {
        return api.get('/crewmembers/active');
    }, []);

    const fetchEntry = useCallback(async () => {
        if (!travelAllowanceEntryId) return null;
        return api.get(`/travelallowanceentries/${travelAllowanceEntryId}`);
    }, [travelAllowanceEntryId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);

                const [countriesRes, airportsRes, crewRes, entryRes] = await Promise.all([
                    fetchCountries(),
                    fetchAirports(),
                    fetchCrewMembers(),
                    fetchEntry(),
                ]);

                setCountries(countriesRes.data || []);
                const airportList = airportsRes.data || [];
                setAirports(airportList);

                const sortedCrew = sortMultipleFields(
                    crewRes.data || [],
                    [
                        { field: 'rank', direction: 'asc' },
                        { field: 'location', direction: 'asc' },
                        { field: 'crewCode', direction: 'asc' },
                    ],
                    true
                );
                setCrewMembers(sortedCrew);

                const d = entryRes?.data;
                if (d?.crewMembers?.length) {
                    const ids = new Set(
                        sortedCrew
                            .filter(cm => d.crewMembers.some(m => m.crewCode === cm.crewCode))
                            .map(cm => cm._id)
                    );
                    setRowSelectionModel({ type: 'include', ids });
                }

                setFormValues({
                    destination: d?.destination || '',
                    travelZone: d?.travelZone || '',
                    isInternational: d?.isInternational ?? false,
                    startDate: d?.startDate ? new Date(d.startDate) : null,
                    endDate: d?.endDate ? new Date(d.endDate) : null,
                    travelHours: d?.travelHours ?? 0,
                    description: d?.description || '',
                    status: d?.status || 'approved',
                });
            } catch (err) {
                setError(err);
                toast.error(err.response?.data?.message || 'Failed to load data!');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [
        fetchCountries, 
        fetchAirports, 
        fetchCrewMembers, 
        fetchEntry
    ]);

    const isSameSet = (a, b) => {
        if (a === b) return true;
        if (!a || !b || a.size !== b.size) return false;
        for (const v of a) if (!b.has(v)) return false;
        return true;
    };

    const handleRowSelectionChange = useCallback((newModel) => {
        if (!newModel?.ids) return;
        setRowSelectionModel(prev => {
            const nextIds = new Set(prev.ids);
            const visibleIds = filteredCrew.map(r => r._id);
            visibleIds.forEach(id => {
                if (prev.ids.has(id) && !newModel.ids.has(id)) nextIds.delete(id);
            });
            newModel.ids.forEach(id => nextIds.add(id));
            if (prev.type === 'include' && isSameSet(prev.ids, nextIds)) return prev;
            return { type: 'include', ids: nextIds };
        });
    }, [filteredCrew]);

    const schema = yup.object({
        destination: yup.string().trim().required("Required"),
        startDate: yup.date().required('Required').typeError('Invalid date'),
        endDate: yup.date().required('Required').typeError('Invalid date'),
    });

    const handleFormSubmit = async (values) => {
        try {
            if (rowSelectionModel.ids.size === 0) {
                setSnackbar({ open: true, message: 'Please select at least one crew member.', severity: 'warning' });
                return;
            }

            const selectedMembers = crewMembers
                .filter(c => rowSelectionModel.ids.has(c._id))
                .map(c => ({
                    crewCode: c.crewCode,
                    crewName: c.crewName,
                    rank: c.rank,
                    base: c.location,
                    division: c.division,
                    department: c.department,
                }));

            const perdiem = calcPerdiemFromHours(values.travelHours, perdiemParams);

            const payload = {
                ...values,
                startDate: values.startDate ? new Date(values.startDate).toISOString() : null,
                endDate: values.endDate ? new Date(values.endDate).toISOString() : null,
                perdiem,
                crewMembers: selectedMembers,
            };

            const url = travelAllowanceEntryId
                ? `/travelallowanceentries/update/${travelAllowanceEntryId}`
                : '/travelallowanceentries/new';
            const method = travelAllowanceEntryId ? 'put' : 'post';

            const res = await api({ method, url, data: payload });

            if (![200, 201].includes(res.status)) {
                toast.error('Failed to submit!');
                return;
            }

            setSnackbar({
                open: true,
                message: `Entry ${travelAllowanceEntryId ? 'updated' : 'created'} for ${rowSelectionModel.ids.size} crew members`,
                severity: 'success',
            });
            navigate('/travelallowanceentries');
        } catch (err) {
            setError(err);
            toast.error(err.message || 'Failed to submit!');
        }
    };

    const crewColumns = [
        { field: 'crewCode', headerName: 'Code', flex: 1 },
        { field: 'crewName', headerName: 'Name', flex: 5 },
        { field: 'rank', headerName: 'Rank', flex: 1.2 },
        { field: 'location', headerName: 'Base', flex: 1.2 },
        { field: 'division', headerName: 'Division', flex: 3 },
        { field: 'department', headerName: 'Department', flex: 3 },
    ];

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Travel Allowance Entry"
                    subtitle={loading ? 'Loading...' : travelAllowanceEntryId ? 'Update' : 'Create'}
                />
                {!loading && (
                    <StyledButton onClick={() => navigate('/travelallowanceentries')}>
                        <ArrowBackIosNewIcon />
                    </StyledButton>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                        <Formik
                            initialValues={formValues}
                            enableReinitialize
                            validationSchema={schema}
                            onSubmit={handleFormSubmit}
                        >
                            {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => {
                                const calculatedPerdiem = calcPerdiemFromHours(values.travelHours, perdiemParams);
                                return (
                                    <form onSubmit={handleSubmit}>
                                        <Box
                                            display="grid"
                                            gap={2}
                                            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                            sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 12' } }}
                                        >
                                            <Autocomplete
                                                freeSolo
                                                options={airports.map(a => a.iataCode)}
                                                value={values.destination}
                                                onChange={(_, newVal) => {
                                                    setFieldValue('destination', newVal || '');
                                                    const ap = airports.find(a => a.iataCode === newVal);
                                                    if (ap) {
                                                        setFieldValue('travelZone', ap.travelZone || '');
                                                        setFieldValue('isInternational', !domesticAirportSet.has(ap.iataCode));
                                                    }
                                                }}
                                                onInputChange={(_, val) => setFieldValue('destination', val)}
                                                renderInput={(params2) => (
                                                    <TextField
                                                        {...params2}
                                                        label="Destination (IATA)"
                                                        name="destination"
                                                        variant="filled"
                                                        size="small"
                                                        fullWidth
                                                    />
                                                )}
                                                sx={{ gridColumn: 'span 3' }}
                                            />

                                            <TextField
                                                label="Travel Zone"
                                                name="travelZone"
                                                variant="filled"
                                                size="small"
                                                fullWidth
                                                value={values.travelZone}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                sx={{ gridColumn: 'span 3' }}
                                            />

                                            <Box sx={{ gridColumn: 'span 3', display: 'flex', alignItems: 'center' }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={values.isInternational}
                                                            name="isInternational"
                                                            disabled
                                                        />
                                                    }
                                                    label="International"
                                                />
                                            </Box>

                                            <TextField
                                                select
                                                label="Status"
                                                name="status"
                                                variant="filled"
                                                size="small"
                                                fullWidth
                                                value={values.status}
                                                onChange={handleChange}
                                                sx={{ gridColumn: 'span 3' }}
                                            >
                                                <MenuItem value="approved">Approved</MenuItem>
                                                <MenuItem value="pending">Pending</MenuItem>
                                            </TextField>

                                            <DateTimePicker
                                                label="Start Date"
                                                value={values.startDate}
                                                format="HH:mm dd/MM/yyyy"
                                                onChange={(val) => {
                                                    setFieldValue('startDate', val);
                                                    if (val && values.endDate) {
                                                        const hours = (new Date(values.endDate) - new Date(val)) / 3600000;
                                                        setFieldValue('travelHours', Math.max(0, parseFloat(hours.toFixed(2))));
                                                    } else {
                                                        setFieldValue('travelHours', 0);
                                                    }
                                                }}
                                                slotProps={{
                                                    field: { clearable: true },
                                                    textField: {
                                                        required: true,
                                                        fullWidth: true,
                                                        variant: 'filled',
                                                        size: 'small',
                                                        error: touched.startDate && Boolean(errors.startDate),
                                                        helperText: touched.startDate && errors.startDate,
                                                    },
                                                }}
                                                sx={{ gridColumn: 'span 3' }}
                                            />

                                            <DateTimePicker
                                                label="End Date"
                                                value={values.endDate}
                                                minDateTime={values.startDate}
                                                format="HH:mm dd/MM/yyyy"
                                                onChange={(val) => {
                                                    setFieldValue('endDate', val);
                                                    if (values.startDate && val) {
                                                        const hours = (new Date(val) - new Date(values.startDate)) / 3600000;
                                                        setFieldValue('travelHours', Math.max(0, parseFloat(hours.toFixed(2))));
                                                    } else {
                                                        setFieldValue('travelHours', 0);
                                                    }
                                                }}
                                                slotProps={{
                                                    field: { clearable: true },
                                                    textField: {
                                                        required: true,
                                                        fullWidth: true,
                                                        variant: 'filled',
                                                        size: 'small',
                                                        error: touched.endDate && Boolean(errors.endDate),
                                                        helperText: touched.endDate && errors.endDate,
                                                    },
                                                }}
                                                sx={{ gridColumn: 'span 3' }}
                                            />

                                            <TextField
                                                label="Travel Hours"
                                                name="travelHours"
                                                type="number"
                                                variant="filled"
                                                size="small"
                                                fullWidth
                                                value={values.travelHours}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.travelHours && Boolean(errors.travelHours)}
                                                helperText={touched.travelHours && errors.travelHours}
                                                slotProps={{
                                                    htmlInput: { style: { textAlign: 'right' } },
                                                }}
                                                sx={{ gridColumn: 'span 3' }}
                                            />

                                            <TextField
                                                label="Perdiem (calculated)"
                                                variant="filled"
                                                size="small"
                                                fullWidth
                                                value={calculatedPerdiem.toFixed(2)}
                                                slotProps={{
                                                    input: { readOnly: true },
                                                    htmlInput: { style: { textAlign: 'right' } },
                                                }}
                                                helperText={`Method: ${perdiemParams.PER_DIEM_CALC_METHOD || 'DAILY'} | Min: ${(Number(perdiemParams.MINIMUM_PERDIEM_MINUTES) || 360) / 60}h`}
                                                sx={{ gridColumn: 'span 3' }}
                                            />

                                            <TextField
                                                label="Description"
                                                name="description"
                                                variant="filled"
                                                size="small"
                                                fullWidth
                                                multiline
                                                rows={1}
                                                value={values.description}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                sx={{ gridColumn: 'span 12' }}
                                            />

                                            <Divider sx={{ gridColumn: 'span 12' }} />

                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Search by Code or Name"
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                sx={{ gridColumn: 'span 3' }}
                                            />
                                            <FormControl size="small" sx={{ gridColumn: 'span 3' }}>
                                                <InputLabel>Rank</InputLabel>
                                                <Select
                                                    value={rankFilter}
                                                    label="Rank"
                                                    onChange={(e) => setRankFilter(e.target.value)}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {sortedRankOptions.map(r => (
                                                        <MenuItem key={r} value={r}>{r}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl size="small" sx={{ gridColumn: 'span 3' }}>
                                                <InputLabel>Base</InputLabel>
                                                <Select
                                                    value={baseFilter}
                                                    label="Base"
                                                    onChange={(e) => setBaseFilter(e.target.value)}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {[...new Set(crewMembers.map(c => c.location).filter(Boolean))].sort().map(b => (
                                                        <MenuItem key={b} value={b}>{b}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl size="small" sx={{ gridColumn: 'span 3' }}>
                                                <InputLabel>Department</InputLabel>
                                                <Select
                                                    value={deptFilter}
                                                    label="Department"
                                                    onChange={(e) => setDeptFilter(e.target.value)}
                                                >
                                                    <MenuItem value="">All</MenuItem>
                                                    {[...new Set(crewMembers.map(c => c.department).filter(Boolean))].sort().map(d => (
                                                        <MenuItem key={d} value={d}>{d}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <DataGridComponent
                                                rows={filteredCrew}
                                                columns={crewColumns}
                                                loading={loading}
                                                props={{
                                                    checkboxSelection: true,
                                                    disableRowSelectionOnClick: true,
                                                    initialState: {
                                                        pagination: { paginationModel: { pageSize: 100 } },
                                                    },
                                                    rowSelectionModel,
                                                    onRowSelectionModelChange: handleRowSelectionChange,
                                                    columnHeaderHeight: 36,
                                                    rowHeight: 30,
                                                }}
                                                sx={{ gridColumn: 'span 12', height: '40vh' }}
                                            />

                                            <Button
                                                fullWidth
                                                type="submit"
                                                variant="contained"
                                                color="secondary"
                                                sx={{ gridColumn: 'span 12', height: 42 }}
                                            >
                                                {travelAllowanceEntryId ? 'Update' : 'Create'} ({rowSelectionModel.ids.size} members)
                                            </Button>
                                        </Box>
                                    </form>
                                );
                            }}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default TravelAllowanceEntryForm;
